import LWElement from './../../lib/lw-element.js';
import ast from './ast.js';
import env from '../../env.js';

customElements.define('web-login',
  class extends LWElement {  // LWElement extends HTMLElement
    constructor() {
      super(ast);
    }

    email = '';
    savedEmail = '';
    rememberMe = false;
    message = '';
    messageType = '';
    emailLoading = false;
    passkeyLoading = false;
    requestId = '';
    code = '';
    codeLoading = false;
    emailCooldown = 0;
    usePhone = false;
    phone = '';
    smsLogin = false;
    smsLoading = false;
    smsCooldown = 0;
    testEnv = false;

    async domReady() {
      this.savedEmail = localStorage.getItem('savedEmail') || '';
      if (this.savedEmail) {
        this.email = this.savedEmail;
        this.rememberMe = true;
      }

      // What this deployment is: whether SMS sign-in is configured, and
      // whether it is a test copy. Public by design — the warning has to
      // reach someone who has no session yet.
      try {
        const response = await fetch(`${env.pubApiUrl}features`);
        if (response.ok) {
          const features = await response.json();
          this.smsLogin = !!features.sms_login;
          this.testEnv = !!features.test_env;
          this.update();
        }
      } catch { /* toggle stays hidden */ }
    }

    onRememberMeChange() {
      if (!this.rememberMe) {
        localStorage.removeItem('savedEmail');
        this.savedEmail = '';
        this.email = '';
      }
    }

    saveEmailIfRemembered() {
      if (this.rememberMe) {
        localStorage.setItem('savedEmail', this.email);
        this.savedEmail = this.email;
      } else {
        localStorage.removeItem('savedEmail');
        this.savedEmail = '';
      }
    }

    async onEmailKeydown(event) {
      // Ignore auto-repeat so holding Enter can't fire a burst of requests.
      if (event.key === 'Enter' && !event.repeat) {
        if (this.savedEmail) {
          await this.loginWithPasskey();
        } else {
          await this.loginWithEmail();
        }
      }
    }

    setMessage(text, type = '') {
      this.message = text;
      this.messageType = type;
    }

    // reset clears the transient sign-in state when the login screen comes
    // back after a logout — lw-if parks this instance rather than destroying
    // it, so a previous request's code entry would otherwise reappear. The
    // remembered email deliberately survives.
    reset() {
      this.setMessage('');
      this.requestId = '';
      this.code = '';
      this.stopEmailCooldown();
      this.stopSmsCooldown();
      this.usePhone = false; // back to the primary path; the typed number survives
    }

    // A successful send disables the email button for 30s — a quick nudge
    // against accidental double-sends before the server's per-address
    // throttle (5 per 15 min) would burn a whole slot.
    startEmailCooldown() {
      this.stopEmailCooldown();
      this.emailCooldown = 30;
      const btn = this.querySelector('.login-email-btn');
      if (btn) btn.disabled = true;
      this._cooldownTimer = setInterval(() => {
        this.emailCooldown--;
        if (this.emailCooldown <= 0) {
          this.stopEmailCooldown();
        }
        this.update();
      }, 1000);
    }

    stopEmailCooldown() {
      clearInterval(this._cooldownTimer);
      this._cooldownTimer = null;
      this.emailCooldown = 0;
      const btn = this.querySelector('.login-email-btn');
      if (btn) btn.disabled = false;
    }

    startSmsCooldown() {
      this.stopSmsCooldown();
      this.smsCooldown = 30;
      const btn = this.querySelector('.login-sms-btn');
      if (btn) btn.disabled = true;
      this._smsCooldownTimer = setInterval(() => {
        this.smsCooldown--;
        if (this.smsCooldown <= 0) {
          this.stopSmsCooldown();
        }
        this.update();
      }, 1000);
    }

    stopSmsCooldown() {
      clearInterval(this._smsCooldownTimer);
      this._smsCooldownTimer = null;
      this.smsCooldown = 0;
      const btn = this.querySelector('.login-sms-btn');
      if (btn) btn.disabled = false;
    }

    // Switching modes clears the transient request state; the typed email
    // and number both survive so flipping back is cheap.
    setPhoneMode(on) {
      this.usePhone = on;
      this.setMessage('');
      this.requestId = '';
      this.code = '';
      this.stopEmailCooldown();
      this.stopSmsCooldown();
      this.update();
      this.querySelector(on ? '#login-phone' : '#login-email')?.focus();
    }

    onPhoneKeydown(event) {
      if (event.key === 'Enter' && !event.repeat) {
        this.loginWithPhone();
      }
    }

    async loginWithPhone() {
      if (this.smsLoading || this.smsCooldown) return;
      if (!this.phone) {
        this.setMessage('Please enter your mobile number', 'danger');
        return;
      }

      this.smsLoading = true;
      this.update();
      try {
        const response = await fetch(`${env.pubApiUrl}login_start_sms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: this.phone })
        });

        const msg = await response.json();
        if (response.ok) {
          this.setMessage(msg);
          this.startSmsCooldown();
          // Same request-id contract as the email flow; the texted code
          // redeems against it in the shared code field below.
          const requestId = response.headers.get('login_request');
          if (requestId) {
            this.requestId = requestId;
            this.code = '';
            this.update();
            this.querySelector('#login-code-input')?.focus();
          }
        } else {
          this.setMessage(msg, 'danger');
        }
      } catch (error) {
        this.setMessage(error.message, 'danger');
      } finally {
        this.smsLoading = false;
      }
    }

    async loginWithEmail() {
      if (this.emailLoading || this.emailCooldown) return;
      if (!this.email) {
        this.setMessage('Please enter your email', 'danger');
        return;
      }

      this.emailLoading = true;
      this.update();
      try {
        const response = await fetch(`${env.pubApiUrl}login_start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // The current hash rides along so the magic link lands back on it
          // (e.g. a ticket opened from a notification email).
          body: JSON.stringify({ email: this.email, redirect: location.hash.slice(1) })
        });

        const msg = await response.json();
        if (response.ok) {
          this.saveEmailIfRemembered();
          this.setMessage(msg);
          this.startEmailCooldown();
          // The header names the pending sign-in request; the emailed code
          // redeems against it right here — no copying links across devices.
          const requestId = response.headers.get('login_request');
          if (requestId) {
            this.requestId = requestId;
            this.code = '';
            this.update();
            this.querySelector('#login-code-input')?.focus();
          }
        } else {
          this.setMessage(msg, 'danger');
        }
      } catch (error) {
        this.setMessage(error.message, 'danger');
      } finally {
        this.emailLoading = false;
      }
    }

    // onCodeInput formats as the user types — uppercase, the display dash
    // auto-inserted after the 4th character — and submits by itself the
    // moment all 8 characters are in (there is no button). The dash is only
    // added once a 5th character exists, so backspacing never fights it.
    onCodeInput(event) {
      const cleaned = event.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 8);
      const formatted = cleaned.length > 4 ? cleaned.slice(0, 4) + '-' + cleaned.slice(4) : cleaned;
      event.target.value = formatted;
      this.code = formatted;
      if (cleaned.length === 8) {
        this.loginWithCode();
      }
    }

    async loginWithCode() {
      if (this.codeLoading) return;
      if (!this.code.trim()) {
        return;
      }

      this.codeLoading = true;
      this.update();
      try {
        const response = await fetch(`${env.pubApiUrl}login_code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request_id: this.requestId, code: this.code.trim() })
        });

        const data = await response.json();
        if (response.ok) {
          sessionStorage.setItem('session', data.sid);
          this.saveEmailIfRemembered();
          this.dispatchEvent(new CustomEvent('login', { bubbles: true, composed: true }));
        } else {
          this.setMessage(data, 'danger');
        }
      } catch (error) {
        this.setMessage(error.message, 'danger');
      } finally {
        this.codeLoading = false;
      }
    }

    async loginWithPasskey() {
      if (this.passkeyLoading) return;
      if (!this.email) {
        this.setMessage('Please enter your email', 'danger');
        return;
      }

      this.passkeyLoading = true;
      this.update();
      try {
        const response = await fetch(`${env.pubApiUrl}passkey_login_start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email })
        });

        if (!response.ok) {
          const msg = await response.json();
          throw new Error('Failed to get login options: ' + msg);
        }

        const loginSid = response.headers.get('login_sid');
        if (!loginSid) {
          throw new Error('No login_sid in response header');
        }

        const options = await response.json();

        const assertionResponse = await SimpleWebAuthnBrowser.startAuthentication({ optionsJSON: options.publicKey });

        const verificationResponse = await fetch(`${env.pubApiUrl}passkey_login_finish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'login_sid': loginSid },
          body: JSON.stringify(assertionResponse)
        });

        const data = await verificationResponse.json();
        if (verificationResponse.ok) {
          sessionStorage.setItem('session', data.sid);
          this.saveEmailIfRemembered();
          this.dispatchEvent(new CustomEvent('login', { bubbles: true, composed: true }));
        } else {
          const msg = data;
          this.setMessage(msg, 'danger');
        }
      } catch (error) {
        this.setMessage(error.message, 'danger');
      } finally {
        this.passkeyLoading = false;
      }
    }
  }
);
